import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { KeyboardArrowLeft } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { PagePaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { usePlotRequestDetail } from "@/features/map/hooks/usePlotRequestDetail";
import { MapPlotRequestDetailFilesView } from "@/features/mapPlotRequest/components/MapPlotRequestDetailFilesView";

const ID_PATTERN = /^[0-9]+$/;

const parseId = (value: string | undefined): number | undefined => {
  if (!value || !ID_PATTERN.test(value)) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
};

export const MapPlotRequestDetailFilesPage: React.FC = () => {
  const { id, detailId } = useParams();
  const navigate = useNavigate();
  const plotRequestId = parseId(id);
  const targetDetailId = parseId(detailId);

  const { data, isLoading, isError } = usePlotRequestDetail(plotRequestId);

  const handleBack = (): void => {
    if (plotRequestId === undefined) {
      navigate("/map", { state: { openSavedPlotModal: true } });
      return;
    }

    navigate(`/mapplotrequest/${plotRequestId}`);
  };

  if (plotRequestId === undefined || targetDetailId === undefined) {
    return (
      <PagePaper>
        <Stack alignItems="center" spacing={2}>
          <Typography color="error">URLが不正です。</Typography>
          <Button
            variant="outlined"
            startIcon={<KeyboardArrowLeft />}
            onClick={handleBack}
          >
            戻る
          </Button>
        </Stack>
      </PagePaper>
    );
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const detail =
    data?.details.find((item) => item.id === targetDetailId) ?? null;

  if (isError || !data || detail === null) {
    return (
      <PagePaper>
        <Stack alignItems="center" spacing={2}>
          <Typography color="error">
            資料対象データの取得に失敗しました。
          </Typography>
          <Button
            variant="outlined"
            startIcon={<KeyboardArrowLeft />}
            onClick={handleBack}
          >
            戻る
          </Button>
        </Stack>
      </PagePaper>
    );
  }

  return (
    <PagePaper>
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Button
            variant="text"
            startIcon={<KeyboardArrowLeft />}
            onClick={handleBack}
          >
            戻る
          </Button>
        </Box>
        <PageTitle>資料管理</PageTitle>
      </Box>

      <Table size="small" sx={{ mb: 3 }}>
        <TableBody>
          <TableRow>
            <TableCell
              component="th"
              sx={{
                width: "12%",
                border: "none",
                p: 1,
                pl: 0,
                pr: 2,
              }}
            >
              案件名
            </TableCell>
            <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
              {data.name}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              component="th"
              sx={{
                width: "12%",
                border: "none",
                p: 1,
                pl: 0,
                pr: 2,
              }}
            >
              行番号
            </TableCell>
            <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
              {detail.detailNumber}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              component="th"
              sx={{
                width: "12%",
                border: "none",
                p: 1,
                pl: 0,
                pr: 2,
              }}
            >
              種別
            </TableCell>
            <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
              {detail.realEstateType ?? "土地"}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              component="th"
              sx={{
                width: "12%",
                border: "none",
                p: 1,
                pl: 0,
                pr: 2,
              }}
            >
              所在
            </TableCell>
            <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
              {detail.requestLocation ?? "-"}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <MapPlotRequestDetailFilesView detail={detail} />
    </PagePaper>
  );
};
